import { ToolsApi } from '@src/services/tools/tools-api';
import { ScriptsDTO } from '@src/services/tools/modules/v1/scripts/dto/brain-scripts.getById.response.dto';
import { BrainApi } from '@src/services/brain/brain-api';
import { TeamMemberItemDTO } from '@src/services/brain/modules/v1/team-members/dto/brain-team-members.get.response.dto';
import {
  ActionDTO,
  GetActionResponseDTO,
  GetActionsParamsDTO,
  GetActionsSummaryResponseDTO,
  ReportDTO,
  GetActionLayersResponseDTO,
} from '@src/services/tools/modules/v1/scripts/dto/tools-requests.dto';
import { ScriptTemplate } from '@src/services/tools/modules/v1/scripts/templates/api-templates';
import { PreSignedUrlResponse } from '@src/services/tools/modules/v1/scripts/requests/documents/api-documents';
import axios from 'axios';
import { getAuthToken } from '@src/services/auth-service';

// Re-export types for external use
export type { ScriptTemplate, PreSignedUrlResponse, ReportDTO };

const client = new ToolsApi();
const brainClient = new BrainApi();

export interface ScriptFilters {
  status?: ('PENDING' | 'RUNNING' | 'BLOCKED' | 'FINISHED' | 'FAILED')[];
  createdByUserId?: string;
  approvedByUserId?: string;
  organizationId?: string;
  startDate?: string;
  endDate?: string;
  name?: string;
  isApproved?: boolean;
}

export interface ActionFilters {
  currentPage: number;
  limit: number;
  sortBy?: 'created_at' | 'started_at' | 'finished_at' | 'status' | 'url' | 'method' | 'layer';
  sortOrder?: 'asc' | 'desc';
  url?: string;
  body?: string;
  status?: string[];
  layer?: number;
}

export const getScripts = async (
  onPageReceived: (scripts: ScriptsDTO[]) => { shouldContinue: boolean },
  filters: ScriptFilters = {},
  specificPage: number,
): Promise<{ items: ScriptsDTO[]; totalPages: number; currentPage: number }> => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const params: Record<string, any> = {
    limit: 10,
    page: specificPage,
  };

  if (filters.status && filters.status.length > 0) {
    params.status = filters.status;
  }
  if (filters.createdByUserId) {
    params.createdByUserId = filters.createdByUserId;
  }
  if (filters.approvedByUserId) {
    params.approvedByUserId = filters.approvedByUserId;
  }
  if (filters.organizationId) {
    params.organizationId = filters.organizationId;
  }
  if (filters.startDate) {
    params.startDate = filters.startDate;
  }
  if (filters.endDate) {
    params.endDate = filters.endDate;
  }
  if (filters.name) {
    params.name = filters.name;
  }
  if (filters.isApproved !== undefined) {
    params.isApproved = filters.isApproved;
  }

  try {
    const response = await client.v1.scripts.requests.get({
      params,
    });

    onPageReceived(response.items);

    return {
      items: response.items,
      totalPages: response.totalPages,
      currentPage: response.currentPage,
    };
  } catch (error) {
    console.error('Error fetching scripts page:', error);
    return {
      items: [],
      totalPages: 1,
      currentPage: 1,
    };
  }
};

export const getScriptById = async (scriptId: string): Promise<ScriptsDTO> => {
  return await client.v1.scripts.requests.getById({
    id: scriptId,
  });
};

export const getActionsSummaryByScriptId = async (executionId: string): Promise<GetActionsSummaryResponseDTO> => {
  return await client.v1.scripts.requests.getActionsSummaryByRequestId({
    id: executionId,
  });
};

export const getLayersByScriptId = async (executionId: string): Promise<GetActionLayersResponseDTO> => {
  return await client.v1.scripts.requests.getActionLayersByRequestId({
    id: executionId,
  });
};

export const getActionsByScriptId = async (
  executionId: string,
  filters: ActionFilters,
): Promise<GetActionResponseDTO> => {
  const params: GetActionsParamsDTO = {
    currentPage: filters.currentPage,
    limit: filters.limit,
    sortBy: filters.sortBy,
    sortOrder: filters.sortOrder,
    status: filters.status,
    body: filters.body,
    url: filters.url,
    layer: filters.layer,
  };

  return await client.v1.scripts.requests.getActionsByRequestId({
    id: executionId,
    params,
  });
};

export const getActionById = async (executionId: string, actionId: string): Promise<ActionDTO> => {
  return await client.v1.scripts.requests.getActionById({
    id: executionId,
    actionId: actionId,
  });
};

export const approveScript = async (scriptId: string): Promise<void> => {
  return await client.v1.scripts.requests.postApprove({
    id: scriptId,
  });
};

export const runScript = async (scriptId: string): Promise<void> => {
  return await client.v1.scripts.requests.postRun({
    id: scriptId,
  });
};

export const cancelScript = async (scriptId: string): Promise<void> => {
  return await client.v1.scripts.requests.postCancel({
    id: scriptId,
  });
};

export const uncancelScript = async (scriptId: string): Promise<void> => {
  return await client.v1.scripts.requests.postUncancel({
    id: scriptId,
  });
};

export const deleteScriptRequest = async (scriptId: string): Promise<void> => {
  return await client.v1.scripts.requests.delete({
    id: scriptId,
  });
};

export const buildUserNamesFromTeamMembers = (members: TeamMemberItemDTO[]): Record<string, string> => {
  return members.reduce(
    (acc, member) => {
      acc[member.userId] = member.name || member.email || member.userId;
      return acc;
    },
    {} as Record<string, string>,
  );
};

export const getTeamMembers = async (): Promise<Array<TeamMemberItemDTO>> => {
  const teamMembers = await brainClient.v1.teamMembers.get({ params: { paginate: false } });
  return teamMembers.items;
};

export const triggerAction = async (scriptId: string, actionId: string): Promise<ActionDTO> => {
  return await client.v1.scripts.requests.postRunAction({
    id: scriptId,
    actionId: actionId,
  });
};

export const exportScript = async (id: string, format: 'csv' | 'xlsx', resolveResponse: boolean): Promise<Blob> => {
  return await client.v1.scripts.requests.export({
    id: id,
    params: {
      format,
      resolveResponse,
    },
  });
};

export const downloadScriptExport = async (
  id: string,
  format: 'csv' | 'xlsx',
  resolveResponse: boolean,
  filename?: string,
): Promise<void> => {
  try {
    const blob = await exportScript(id, format, resolveResponse);

    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;

    link.download = filename || `script-export-${id}.${format}`;

    document.body.appendChild(link);
    link.click();

    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading script export:', error);
    throw error;
  }
};

export const updateExecutionMilliseconds = async (id: string, milliseconds: number): Promise<void> => {
  await client.v1.scripts.requests.put({
    id: id,
    body: { sleepTimeMs: milliseconds },
  });
};

// Canonical Script Creation Hooks

export const getTemplatesByOrganization = async (organizationId: string): Promise<ScriptTemplate[]> => {
  return await client.v1.scripts.templates.getByOrganization(organizationId);
};

export const getPreSignedUploadUrl = async (
  fileName: string,
  contentType: string,
  organizationId: string,
): Promise<PreSignedUrlResponse> => {
  return await client.v1.scripts.requests.documents.postPreSignedUrl({
    body: {
      fileName,
      contentType,
      organizationId,
    },
  });
};

export const uploadFileToS3 = async (uploadUrl: string, file: File): Promise<void> => {
  await axios.put(uploadUrl, file, {
    headers: {
      'Content-Type': file.type,
    },
  });
};

export const implementCanonicalScript = async (
  templateKey: string,
  organizationId: string,
  documentPath?: string,
  schemaData?: object,
): Promise<void> => {
  const SCRIPTS_BASE_URL = import.meta.env.VITE_SCRIPTS_BASE_URL || '';
  const url = `${SCRIPTS_BASE_URL}/webhooks/templates/${templateKey}/implement`;

  const setup: Record<string, object | string> = {};

  if (documentPath) {
    setup.documentPath = documentPath;
  }

  if (schemaData) {
    setup.config = schemaData;
  }

  await axios.post(
    url,
    {
      organizationId,
      setup,
    },
    {
      headers: {
        Authorization: `Bearer ${await getAuthToken()}`,
      },
    },
  );
};

// Report Extraction Hooks

export const getReports = async (scriptId: string): Promise<ReportDTO[]> => {
  return await client.v1.scripts.requests.getReports({ id: scriptId });
};

export const requestExtraction = async (scriptId: string): Promise<void> => {
  await client.v1.scripts.requests.postExtract({ id: scriptId });
};

export const downloadFromBucket = async (key: string, filename: string): Promise<void> => {
  try {
    // 1. Get pre-signed download URL
    const { downloadUrl } = await client.v1.scripts.requests.documents.getPresignedDownloadUrl({
      params: { key },
    });

    // 2. Download file using the pre-signed URL
    const response = await axios.get(downloadUrl, {
      responseType: 'blob',
    });

    // 3. Create download link
    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading report:', error);
    throw error;
  }
};
